import {
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnChanges,
    Output,
    SimpleChanges,
    Renderer2,
    ComponentFactoryResolver,
    ViewContainerRef,
    ComponentRef,
    Injector,
    ApplicationRef
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';

import { HttpClientWithInFlightCache } from '../../../global';
import { MapItemsFn, SortOptionsFn } from '../form-field';

import { AutoCompleteComponent } from './autocomplete.component';

@Directive({
    standalone: false,
    selector: '[absAutoComplete]'
})
export class AutoCompleteDirective implements OnDestroy {
    @HostListener('keydown', ['$event']) keyDown(e) {
        if (e.keyCode === 13) {
            // Enter
            this.hide();
        } else if (e.keyCode === 27) {
            // Escape
            this.hide();
        } else if (e.keyCode === 40) {
            // Arrow down
            this.adjustSelectedItem$.next(1);
            e.preventDefault();
        } else if (e.keyCode === 38) {
            // Arrow up
            this.adjustSelectedItem$.next(-1);
            e.preventDefault();
        }
    }

    @HostListener('input', ['$event']) input(e) {
        this.inputEvent$.next(e);
    }

    @HostListener('blur') onBlur() {
        // Delay slightly to allow clicks on popover items to register first
        setTimeout(() => this.hide(), 150);
    }

    @HostListener('window:resize') onResize() {
        if (this.popoverElement) {
            this.positionPopover();
        }
    }

    @HostListener('window:scroll') onScroll() {
        if (this.popoverElement) {
            this.positionPopover();
        }
    }

    popoverElement: HTMLDivElement;
    componentRef: ComponentRef<AutoCompleteComponent>;

    @Input()
    options: any[];

    @Input()
    mapOptionsFn: MapItemsFn;

    @Input()
    sortOptionsFn: SortOptionsFn;

    inputEvent$ = new BehaviorSubject(null);
    matchingOptions$: Observable<any[]>;
    matchingOptionsSub: Subscription;
    adjustSelectedItem$ = new BehaviorSubject(0);

    _selectedIndex = -1;
    selectedIndex$: Observable<number>;

    @Output()
    selectionUpdate = new EventEmitter<string>();

    @Output()
    commitSelection = new EventEmitter<string>();

    constructor(
        private http: HttpClientWithInFlightCache,
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private componentFactoryResolver: ComponentFactoryResolver,
        private injector: Injector,
        private appRef: ApplicationRef
    ) {
        this.matchingOptions$ = this.inputEvent$.pipe(
            map((e) => {
                if (!e) {
                    return [];
                }

                const value = e.target.value;
                const matchingOptions = this.getMatches(this.options || [], value);

                if (matchingOptions.length === 0) {
                    this.hide();
                } else if (!this.popoverElement) {
                    this.show(e);
                }

                if (this._selectedIndex > matchingOptions.length - 1) {
                    this._selectedIndex = -1;
                }

                return matchingOptions;
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );
        this.matchingOptionsSub = this.matchingOptions$.subscribe();

        this.selectedIndex$ = combineLatest([this.matchingOptions$, this.adjustSelectedItem$.pipe(filter((i) => i !== 0))]).pipe(
            map(([matchingOptions, adjustment]) => {
                if (adjustment === 0) {
                    return this._selectedIndex;
                }

                if (
                    (adjustment > 0 && adjustment + this._selectedIndex <= matchingOptions.length - 1) ||
                    (adjustment < 0 && adjustment + this._selectedIndex > -1)
                ) {
                    this._selectedIndex += adjustment;
                }
                this.adjustSelectedItem$.next(0);
                const opt = matchingOptions[this._selectedIndex];
                this.selectionUpdate.emit(opt?.value || opt || '');
                return this._selectedIndex;
            })
        );
    }

    private getMatches(options, value) {
        const isMatch = (val) => val?.toLowerCase && val?.toLowerCase().includes(value.toLowerCase());
        return options
            .filter((opt) => (opt && isMatch(opt.label)) || isMatch(opt.value) || isMatch(opt))
            .sort((a, b) => {
                if (this.sortOptionsFn) {
                    return this.sortOptionsFn(a, b);
                }
                return 0;
            });
    }

    show(e: any) {
        if (!this.popoverElement) {
            // Create a div for the popover
            this.popoverElement = this.renderer.createElement('div');
            this.renderer.setStyle(this.popoverElement, 'position', 'absolute');
            this.renderer.setStyle(this.popoverElement, 'z-index', '10000');
            this.renderer.setStyle(this.popoverElement, 'background', 'white');
            this.renderer.setStyle(this.popoverElement, 'border', '1px solid #ccc');
            this.renderer.setStyle(this.popoverElement, 'border-radius', '4px');
            this.renderer.setStyle(this.popoverElement, 'box-shadow', '0 2px 8px rgba(0,0,0,0.15)');
            this.renderer.setStyle(this.popoverElement, 'max-height', '300px');
            this.renderer.setStyle(this.popoverElement, 'overflow-y', 'auto');
            this.renderer.setStyle(this.popoverElement, 'min-width', '200px');

            // Create the component
            const componentFactory = this.componentFactoryResolver.resolveComponentFactory(AutoCompleteComponent);
            this.componentRef = componentFactory.create(this.injector, [], this.popoverElement);

            // Set the component inputs
            this.componentRef.instance.options$ = this.matchingOptions$;
            this.componentRef.instance.selectedIndex$ = this.selectedIndex$;

            // Subscribe to selection events
            const component = this.componentRef.instance;
            const originalDismiss = component.dismiss.bind(component);
            component.dismiss = (value) => {
                this.commitSelection.emit(value?.value || value);
                this.hide();
            };

            // Attach component to the app
            this.appRef.attachView(this.componentRef.hostView);

            // Add to DOM
            document.body.appendChild(this.popoverElement);

            // Position it
            this.positionPopover();
        }
    }

    positionPopover() {
        if (!this.popoverElement) return;

        const inputRect = this.elementRef.nativeElement.getBoundingClientRect();
        this.renderer.setStyle(this.popoverElement, 'left', `${inputRect.left}px`);
        this.renderer.setStyle(this.popoverElement, 'top', `${inputRect.bottom + 2}px`);
        this.renderer.setStyle(this.popoverElement, 'width', `${inputRect.width}px`);
    }

    hide() {
        this._selectedIndex = -1;
        if (this.popoverElement) {
            this.appRef.detachView(this.componentRef.hostView);
            this.componentRef.destroy();
            this.renderer.removeChild(document.body, this.popoverElement);
            this.popoverElement = null;
            this.componentRef = null;
        }
    }

    ngOnDestroy() {
        if (this.matchingOptionsSub) {
            this.matchingOptionsSub.unsubscribe();
        }
        this.hide();
    }
}
